import apiClient from './api';

const BASE_URL = '/exports';

export interface ExportFilters {
  dateDebut?: string;
  dateFin?: string;
  compagnie?: number;
  format?: 'json' | 'csv';
}

export interface FacturationData {
  compagnie: string;
  codeIata: string;
  nombreInterventions: number;
  dureeMinutes: number;
}

export interface KpiData {
  totalInterventions: number;
  enCours: number;
  terminees: number;
  validees: number;
  annulees: number;
  tempsMoyenMinutes: number;
  tauxValidation: number;
  tauxAnnulation: number;
  parPriorite: Record<string, number>;
  parType: Record<string, number>;
  parJour: Record<string, number>;
}

export interface RapportJournalier {
  date: string;
  totalInterventions: number;
  planifiees: number;
  enCours: number;
  terminees: number;
  validees: number;
  annulees: number;
  interventions: Array<{
    numeroOrdre: string;
    compagnie: string;
    parkings: string;
    statut: string;
    heureDebut?: string;
    heureFin?: string;
    duree?: number;
  }>;
}

/**
 * Service API pour les exports
 */
export const exportService = {
  /**
   * Export facturation par compagnie
   */
  async getFacturation(filters: ExportFilters = {}): Promise<FacturationData[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<FacturationData[]>(
      `${BASE_URL}/facturation?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Telecharger facturation en CSV
   */
  async downloadFacturationCsv(filters: ExportFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', 'csv');
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'format') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get(`${BASE_URL}/facturation?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Export KPI
   */
  async getKpi(filters: ExportFilters = {}): Promise<KpiData> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<KpiData>(
      `${BASE_URL}/kpi?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Rapport journalier
   */
  async getRapportJournalier(date?: string): Promise<RapportJournalier> {
    const params = date ? `?date=${date}` : '';
    const response = await apiClient.get<RapportJournalier>(
      `${BASE_URL}/rapport-journalier${params}`
    );
    return response.data;
  },

  /**
   * Export interventions
   */
  async getInterventions(filters: ExportFilters = {}): Promise<unknown[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<unknown[]>(
      `${BASE_URL}/interventions?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Telecharger interventions en CSV
   */
  async downloadInterventionsCsv(filters: ExportFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', 'csv');
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'format') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get(`${BASE_URL}/interventions?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

/**
 * Utilitaire pour telecharger un fichier blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default exportService;
